import React from 'react';
import CircleProgress from '../Common/CircleProgress';
import {
  GotoIcon,
  LocationIcon,
  GameModeIcon,
  PerformanceScoreIcon,
} from '../Common/Icons';
import { Button, Card, Row, Col } from 'react-bootstrap';
import { nth } from '../../utils/formatter';

const LevelSelector = ({ game, changeGameLevel, competition }) => {
  if (!game.history) {
    return (
      <span className="text-muted">
        <em>Loading, please wait...</em>
      </span>
    );
  }

  const totalPerformanceScore = game.history.reduce(
    (total, curr) => total + curr.performanceScore,
    0
  );
  const totalPerformancePct = Math.round(totalPerformanceScore / 5);

  return (
    <>
      <div className="text-center">
        <h4>Congratulations, you've made it to the final level!</h4>
      </div>

      <p className="mb-1">
        Great work! You can now <b>go to any level at any time</b> to try and
        improve your <b>performance score</b> for that level. This score is
        based on your best design for each level, so it can only go up!
      </p>
      <p className="fst-italic">
        NOTE: Going to a different level will close any unsaved designs. Make
        sure your work is saved before moving.
      </p>

      <Row className="mb-3 px-3">
        {game.history &&
          game.history.map((history) => (
            <Col className="p-1 text-center" key={history.level.id}>
              <div className="pb-3">
                <div className="p-3">
                  <CircleProgress
                    primary={history.performanceScore}
                    success={history.performanceScore > 50}
                  >
                    <span className="fs-3">{history.performanceScore}</span>
                  </CircleProgress>
                </div>

                <h5>Level {history.level.level}</h5>

                {game.level.level === history.level.level ? (
                  <>
                    <LocationIcon className="me-1" />
                    You are here
                  </>
                ) : (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="py-1"
                    onClick={() => changeGameLevel(history.level.id)}
                  >
                    Go to Level {history.level.level}
                    <GotoIcon className="ms-1" />
                  </Button>
                )}
              </div>
            </Col>
          ))}

        <Col className="p-1 text-center">
          <div className="pb-3 bg-light border rounded">
            <div className="p-3">
              <CircleProgress primary={totalPerformancePct}>
                <span className="fs-3">{totalPerformancePct}</span>
              </CircleProgress>
            </div>

            <h5>Total</h5>

            <div className="p-1">Cumulative Performance Score</div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="d-flex border rounded p-3">
            <PerformanceScoreIcon className="fs-3 me-3" />
            <div>
              <h5 className="d-flex align-items-center fw-light">
                <span className="fw-light">
                  Total Performance Score:{' '}
                  <strong>{totalPerformancePct}</strong>
                </span>
              </h5>

              <p>
                Your <b>Total Performance Score</b> is based on your cumulative
                performance score across all levels.
              </p>
              <p>
                You have scored <strong>{totalPerformanceScore}</strong> out of
                a possible <strong>500 points</strong> (
                <strong>{totalPerformancePct}%</strong>).
              </p>
              <p>
                Your position among your fellow <em>"Final Level"</em> peers is{' '}
                <strong>{nth(game.ranking.rank)}</strong> out of{' '}
                <strong>{game.ranking.totalLastLevelCompetitors}</strong>,
                placing you in the{' '}
                <strong>{nth(game.ranking.lastLevelPercentile)}</strong>{' '}
                percentile. Improve your design performance from each level to
                stand out among the best of the best!
              </p>
            </div>
          </div>
        </Col>
        <Col>
          <div className="d-flex border rounded p-3">
            <GameModeIcon className="fs-3 me-3" />
            <div>
              <Card.Title className="d-flex align-items-center">
                <span className="fw-light">
                  {competition ? 'Competition Rank' : 'National Rank'}:{' '}
                  <strong>{nth(game.ranking.rank)}</strong>
                </span>
              </Card.Title>

              {competition ? (
                <p>
                  Your <b>Competition Rank</b> is your position in the{' '}
                  {competition.name}
                  competition. Competition rankings are updated every 5 minutes.
                </p>
              ) : (
                <p>
                  Your <b>National Rank</b> is your position among all the
                  students in the WhiteBox Learning world. National rankings are
                  updated every 5 minutes and includes students who have
                  participated in the competition within the last 10 days.
                </p>
              )}
              <p>
                You are <strong>{nth(game.ranking.rank)}</strong> out of{' '}
                <strong>{game.ranking.totalCompetitors}</strong> total
                competitors, placing you in the{' '}
                <strong>{nth(game.ranking.overallPercentile)}</strong>{' '}
                percentile - which means you have scored higher than{' '}
                <strong>{100 - game.ranking.overallPercentile}%</strong> of all
                competitors!
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default LevelSelector;
